"use client"

import type React from "react"

import { ReactNode, useEffect, useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Plus, Trash2, Edit, UsersIcon, X, KeyRound, Search } from "lucide-react"
//import { mockFacilities } from "@/lib/mock-org-data"
import type { User, Facility } from "@/types"
import { Badge } from "@/components/ui/badge"
import OrgLayout from "@/layouts/OrgLayout"
import { getCSRFToken } from "@/lib/utils"
import { createRefreshChannel } from "@/channels/refresh_channel"
//import { useToast } from "@/hooks/use-toast"

export interface UsersProps {
  users: User[]
  facilities: Facility[]
}

function Users({users: initialUsers, facilities = []}: UsersProps) {
  const [users, setUsers] = useState<User[]>(initialUsers || [])
  const [searchQuery, setSearchQuery] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<User | null>(null)
  const [selectedFacilities, setSelectedFacilities] = useState<number[]>([])
  const [facilitySearch, setFacilitySearch] = useState("")
  const [selectAllFacilities, setSelectAllFacilities] = useState(false)
  //const { toast } = useToast()

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const email = formData.get("email") as string
    const name = formData.get("name") as string
    const role = formData.get("role") as string

    const facilitiesToAssign = selectAllFacilities ? facilities.map((f) => f.id) : selectedFacilities

    const csrfToken = getCSRFToken()
    if (editing) {
      setUsers(
        users.map((u) =>
          u.id === editing.id
            ? {
                ...u,
                email,
                name,
                role,
                facility_ids: facilitiesToAssign,
              }
            : u,
        ),
      )
      fetch(`/user/${editing.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify(editing),
      })

    } else {
      const newUser: Partial<User> = {
        email,
        name,
        role,
        archive_global: false,
        facility_ids: facilitiesToAssign

      }
      console.log("New user to be created:", newUser)
      fetch("/user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify(newUser),
      })

    }
    setDialogOpen(false)
    setEditing(null)
    setSelectedFacilities([])
    setSelectAllFacilities(false)
  }

  const handlePasswordReset = (user: User) => {
    user
    //toast({
    //  title: "Password reset",
    //  description: `A password reset email has been sent to ${user.email}`,
    //})
  }

  const addFacility = (facilityId: number) => {
    if (!selectedFacilities.includes(facilityId)) {
      setSelectedFacilities([...selectedFacilities, facilityId])
    }
    setFacilitySearch("")
  }

  const removeFacility = (facilityId: number) => {
    setSelectedFacilities(selectedFacilities.filter((id:number) => id !== facilityId))
  }

  const filteredFacilities = facilities.filter(
    (f) => f?.name?.toLowerCase().includes(facilitySearch?.toLowerCase()) && !selectedFacilities.includes(f.id),
  )

  const handleDelete = (id: number) => {
    setUsers(users.filter((u) => u.id !== id))
    fetch(`/user/${id}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": getCSRFToken() || "",
      },
    })
  }
  useEffect(() => {
    createRefreshChannel(['users'])

  }, [])

  const filteredUsers = users.filter((user) => {
    const query = searchQuery?.toLowerCase()
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    )
  })

  return (
    <div className="space-y-6 w-full">
      <div className="flex w-full items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Users</h2>
          <p className="text-sm text-muted-foreground">Manage user accounts and facility assignments</p>
        </div>
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open)
            if (!open) {
              setEditing(null)
              setSelectedFacilities([])
              setSelectAllFacilities(false)
            }
          }}
        >
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditing(null)
                setSelectedFacilities([])
                setSelectAllFacilities(false)
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSave}>
              <DialogHeader>
                <DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle>
                <DialogDescription>
                  {editing
                    ? "Update user information"
                    : "Create a new user account. A password will be emailed to the user."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" name="name" defaultValue={editing?.name} placeholder="John Doe" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    defaultValue={editing?.email}
                    placeholder="john@clinic.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <select
                    name="role"
                    defaultValue={editing?.role || "user"}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="billing_admin" disabled>Billing Admin</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="facility_admin">Facility Admin</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Assigned Facilities</Label>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="select-all"
                        checked={selectAllFacilities}
                        onCheckedChange={(checked) => {
                          setSelectAllFacilities(checked as boolean)
                          if (checked) {
                            setSelectedFacilities([])
                          }
                        }}
                      />
                      <label htmlFor="select-all" className="text-sm cursor-pointer">
                        Select All Facilities
                      </label>
                    </div>
                  </div>
                  {selectAllFacilities ? (
                    <div className="p-3 border rounded-md bg-muted/30">
                      <p className="text-sm text-muted-foreground">All facilities selected ({facilities.length})</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedFacilities.length > 0 && (
                        <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/30 max-h-32 overflow-y-auto">
                          {selectedFacilities.map((facilityId) => {
                            const facility = facilities.find((f) => f.id === facilityId)
                            return facility ? (
                              <Badge key={facilityId} variant="secondary" className="gap-1">
                                {facility.name}
                                <button
                                  type="button"
                                  onClick={() => removeFacility(facilityId)}
                                  className="ml-1 hover:bg-destructive/20 rounded-full"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ) : null
                          })}
                        </div>
                      )}
                      <div className="relative">
                        <Input
                          placeholder="Search facilities to add..."
                          value={facilitySearch}
                          onChange={(e) => setFacilitySearch(e.target.value)}
                        />
                        {facilitySearch && filteredFacilities.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                            {filteredFacilities.map((facility) => (
                              <button
                                key={facility.id}
                                type="button"
                                onClick={() => addFacility(facility.id)}
                                className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                              >
                                {facility.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">{editing ? "Update" : "Create"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative flex items-center gap-2">
        <Search className=" h-4 w-4  text-muted-foreground" />
        <Input
          placeholder="Search users by name, email, or role..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="space-y-2">
        {filteredUsers.map((user) => {
          const user_facilities = facilities.filter((f) => user.facility_ids?.includes(f.id))
          return (
            <Card key={user.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <UsersIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{user.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-muted-foreground">
                        {user.email} • {user.role}
                      </p>
                      {user_facilities.length > 0 && (
                        <>
                          <span className="text-muted-foreground">•</span>
                          <div className="flex flex-wrap gap-1">
                            {user_facilities.slice(0, 3).map((f) => (
                              <Badge key={f.id} variant="outline" className="text-xs">
                                {f.name}
                              </Badge>
                            ))}
                            {user_facilities.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{user_facilities.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handlePasswordReset(user)} title="Reset password">
                    <KeyRound className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditing(user)
                      setSelectedFacilities(user.facility_ids || [])
                      setDialogOpen(true)
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
Users.layout = (page: ReactNode) => <OrgLayout activeSection="users">{page}</OrgLayout>
export default Users
